import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import type { Tables, TablesInsert } from "@/gestionale/integrations/supabase/types";
import type { Database } from "@/gestionale/integrations/supabase/types";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/gestionale/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/gestionale/components/ui/form";
import { Input } from "@/gestionale/components/ui/input";
import { Textarea } from "@/gestionale/components/ui/textarea";
import { Button } from "@/gestionale/components/ui/button";
import { Checkbox } from "@/gestionale/components/ui/checkbox";
import { Label } from "@/gestionale/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/gestionale/components/ui/popover";
import { Calendar } from "@/gestionale/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";
import { cn } from "@/gestionale/lib/utils";

type TipoRuolo = Database["public"]["Enums"]["tipo_ruolo"];

const RUOLI_OPTIONS: TipoRuolo[] = ["Dirigente", "Socio", "Abbonato", "Atleta", "Giocatore", "Allenatore", "Istruttore", "Genitore"];

const PIEDE_OPTIONS = ["Destro", "Sinistro", "Ambidestro"] as const;

const CURRENT_YEAR = new Date().getFullYear();

// Converte una stringa "YYYY-MM-DD" in un oggetto Date locale,
// evitando i problemi di fuso orario che si hanno con `new Date(stringaISO)`
// (che interpreta la stringa come UTC e può "spostare" la data di un giorno).
function parseISODate(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const parts = value.split("-").map(Number);
  const [y, m, d] = parts;
  if (!y || !m || !d) return undefined;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? undefined : date;
}

// Converte un oggetto Date in stringa "YYYY-MM-DD" per il salvataggio,
// sempre in orario locale (nessuno slittamento di fuso orario).
function toISODate(date?: Date): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Valida che una stringa "YYYY-MM-DD" sia una data reale ed esistente
// (es. rifiuta 31/02, 00/00, mesi > 12, ecc.) ed entro un range plausibile.
function makeDateValidator(minYear: number, maxYear: number) {
  return (val: string | undefined) => {
    if (!val) return true; // campo opzionale, vuoto è sempre valido
    const date = parseISODate(val);
    if (!date) return false;
    const year = date.getFullYear();
    return year >= minYear && year <= maxYear;
  };
}

const schema = z.object({
  nome: z.string().trim().min(1, "Nome obbligatorio").max(100),
  cognome: z.string().trim().min(1, "Cognome obbligatorio").max(100),
  codice_fiscale: z.string().trim().max(16).optional().or(z.literal("")),
  data_nascita: z.string().optional().or(z.literal(""))
    .refine(makeDateValidator(1900, CURRENT_YEAR), "Data di nascita non valida"),
  telefono: z.string().trim().max(20).optional().or(z.literal("")),
  email: z.string().trim().email("Email non valida").max(255).optional().or(z.literal("")),
  indirizzo: z.string().trim().max(255).optional().or(z.literal("")),
  note: z.string().trim().max(1000).optional().or(z.literal("")),
  certificato_medico_scadenza: z.string().optional().or(z.literal(""))
    .refine(makeDateValidator(2000, CURRENT_YEAR + 10), "Data scadenza non valida"),
  // Scheda giocatore
  ruolo_principale: z.string().trim().max(50).optional().or(z.literal("")),
  ruolo_secondario: z.string().trim().max(50).optional().or(z.literal("")),
  piede_preferito: z.string().trim().max(20).optional().or(z.literal("")),
  altezza_cm: z.string().trim().optional().or(z.literal(""))
    .refine((val) => !val || /^\d{2,3}$/.test(val), "Altezza non valida (cm)"),
  peso_kg: z.string().trim().optional().or(z.literal(""))
    .refine((val) => !val || /^\d{2,3}$/.test(val), "Peso non valido (kg)"),
  pregi: z.string().trim().max(500).optional().or(z.literal("")),
  giocatore_ispirazione: z.string().trim().max(200).optional().or(z.literal("")),
  squadra_cuore: z.string().trim().max(200).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_VALUES: FormValues = {
  nome: "", cognome: "", codice_fiscale: "", data_nascita: "",
  telefono: "", email: "", indirizzo: "", note: "", certificato_medico_scadenza: "",
  ruolo_principale: "", ruolo_secondario: "", piede_preferito: "",
  altezza_cm: "", peso_kg: "", pregi: "", giocatore_ispirazione: "", squadra_cuore: "",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: Tables<"persone"> | null;
  personaRuoli: TipoRuolo[];
  onSave: (data: TablesInsert<"persone"> & { id?: string }, ruoli: TipoRuolo[]) => void;
  isSaving: boolean;
}

export default function PersonaDialog({ open, onOpenChange, persona, personaRuoli, onSave, isSaving }: Props) {
  const [selectedRuoli, setSelectedRuoli] = useState<TipoRuolo[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (open) {
      if (persona) {
        form.reset({
          nome: persona.nome,
          cognome: persona.cognome,
          codice_fiscale: persona.codice_fiscale || "",
          data_nascita: persona.data_nascita || "",
          telefono: persona.telefono || "",
          email: persona.email || "",
          indirizzo: persona.indirizzo || "",
          note: persona.note || "",
          certificato_medico_scadenza: persona.certificato_medico_scadenza || "",
          ruolo_principale: (persona as any).ruolo_principale || "",
          ruolo_secondario: (persona as any).ruolo_secondario || "",
          piede_preferito: (persona as any).piede_preferito || "",
          altezza_cm: (persona as any).altezza_cm != null ? String((persona as any).altezza_cm) : "",
          peso_kg: (persona as any).peso_kg != null ? String((persona as any).peso_kg) : "",
          pregi: (persona as any).pregi || "",
          giocatore_ispirazione: (persona as any).giocatore_ispirazione || "",
          squadra_cuore: (persona as any).squadra_cuore || "",
        });
        setSelectedRuoli(personaRuoli);
      } else {
        form.reset(EMPTY_VALUES);
        setSelectedRuoli([]);
      }
    }
  }, [open, persona, personaRuoli, form]);

  const toggleRuolo = (ruolo: TipoRuolo) => {
    setSelectedRuoli((prev) =>
      prev.includes(ruolo) ? prev.filter((r) => r !== ruolo) : [...prev, ruolo]
    );
  };

  const isGiocatore = selectedRuoli.includes("Giocatore");

  const onSubmit = (values: FormValues) => {
    const data: TablesInsert<"persone"> & { id?: string } = {
      nome: values.nome,
      cognome: values.cognome,
      codice_fiscale: values.codice_fiscale ? values.codice_fiscale.toUpperCase() : null,
      data_nascita: values.data_nascita || null,
      telefono: values.telefono || null,
      email: values.email || null,
      indirizzo: values.indirizzo || null,
      note: values.note || null,
      certificato_medico_scadenza: values.certificato_medico_scadenza || null,
      ruolo_principale: values.ruolo_principale || null,
      ruolo_secondario: values.ruolo_secondario || null,
      piede_preferito: values.piede_preferito || null,
      altezza_cm: values.altezza_cm ? parseInt(values.altezza_cm, 10) : null,
      peso_kg: values.peso_kg ? parseInt(values.peso_kg, 10) : null,
      pregi: values.pregi || null,
      giocatore_ispirazione: values.giocatore_ispirazione || null,
      squadra_cuore: values.squadra_cuore || null,
    } as TablesInsert<"persone"> & { id?: string };
    if (persona) data.id = persona.id;
    onSave(data, selectedRuoli);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{persona ? "Modifica Persona" : "Nuova Persona"}</DialogTitle>
          <DialogDescription>
            {persona ? "Modifica i dati della persona" : "Compila i dati per aggiungere una nuova persona"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField control={form.control} name="nome" render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="cognome" render={({ field }) => (
                <FormItem>
                  <FormLabel>Cognome *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="codice_fiscale" render={({ field }) => (
                <FormItem>
                  <FormLabel>Codice Fiscale</FormLabel>
                  <FormControl><Input {...field} maxLength={16} className="uppercase" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              {/* Data di Nascita - calendario visuale con selezione rapida anno/mese */}
              <FormField control={form.control} name="data_nascita" render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Data di Nascita</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                          {field.value ? (
                            format(parseISODate(field.value)!, "dd/MM/yyyy", { locale: it })
                          ) : (
                            <span>Seleziona data</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={parseISODate(field.value)}
                        onSelect={(date) => field.onChange(toISODate(date))}
                        disabled={(date) => date > new Date() || date < new Date(1900, 0, 1)}
                        captionLayout="dropdown"
                        startMonth={new Date(1900, 0)}
                        endMonth={new Date(CURRENT_YEAR, 11)}
                        locale={it as any}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="telefono" render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefono</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl><Input type="email" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="indirizzo" render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Indirizzo</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              {/* Scadenza Certificato Medico - calendario visuale con selezione rapida anno/mese */}
              <FormField control={form.control} name="certificato_medico_scadenza" render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Scadenza Certificato Medico</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                          {field.value ? (
                            format(parseISODate(field.value)!, "dd/MM/yyyy", { locale: it })
                          ) : (
                            <span>Seleziona data</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={parseISODate(field.value)}
                        onSelect={(date) => field.onChange(toISODate(date))}
                        captionLayout="dropdown"
                        startMonth={new Date(2000, 0)}
                        endMonth={new Date(CURRENT_YEAR + 10, 11)}
                        locale={it as any}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            {/* Ruoli section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Ruoli</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {RUOLI_OPTIONS.map((ruolo) => (
                  <label
                    key={ruolo}
                    className="flex items-center gap-2 rounded-md border p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedRuoli.includes(ruolo)}
                      onCheckedChange={() => toggleRuolo(ruolo)}
                    />
                    <span className="text-sm">{ruolo}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Scheda giocatore - visibile solo se il ruolo "Giocatore" è selezionato */}
            {isGiocatore && (
              <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                <Label className="text-sm font-medium">Scheda giocatore</Label>
                <p className="text-xs text-muted-foreground -mt-2">
                  Questi dati vengono mostrati anche nella figurina pubblica sul sito.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="ruolo_principale" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ruolo principale</FormLabel>
                      <FormControl><Input {...field} placeholder="Es. Attaccante" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="ruolo_secondario" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ruolo secondario</FormLabel>
                      <FormControl><Input {...field} placeholder="Es. Centrocampista" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="piede_preferito" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Piede preferito</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PIEDE_OPTIONS.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="altezza_cm" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Altezza (cm)</FormLabel>
                        <FormControl><Input {...field} inputMode="numeric" placeholder="180" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="peso_kg" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Peso (kg)</FormLabel>
                        <FormControl><Input {...field} inputMode="numeric" placeholder="75" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="giocatore_ispirazione" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Giocatore a cui si ispira</FormLabel>
                      <FormControl><Input {...field} placeholder="Es. Roberto Baggio" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="squadra_cuore" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Squadra del cuore</FormLabel>
                      <FormControl><Input {...field} placeholder="Es. Juventus" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="pregi" render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Pregi</FormLabel>
                      <FormControl><Textarea rows={2} {...field} placeholder="Es. Velocità, precisione nei calci piazzati..." /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            <FormField control={form.control} name="note" render={({ field }) => (
              <FormItem>
                <FormLabel>Note</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Salvataggio..." : persona ? "Salva modifiche" : "Crea persona"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

